import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { Observable, Subject, takeUntil } from 'rxjs';
import { DIALOG_CONFIG, SMALL_DIALOG_CONFIG, MEDIUM_DIALOG_CONFIG, LARGE_DIALOG_CONFIG } from '../config/dialog.config';

/**
 * Service for managing dialogs with automatic subscription cleanup.
 * Provides standardized dialog configurations and ensures proper cleanup.
 *
 * @example
 * ```typescript
 * export class MyComponent {
 *   private dialogManager = inject(DialogManagerService);
 *   private destroy$ = new Subject<void>();
 *
 *   openDialog(): void {
 *     this.dialogManager.openMedium(
 *       MyDialogComponent,
 *       { data: myData },
 *       this.destroy$
 *     ).subscribe(result => {
 *       if (result) { ... }
 *     });
 *   }
 *
 *   ngOnDestroy(): void {
 *     this.destroy$.next();
 *     this.destroy$.complete();
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DialogManagerService {
  private dialog = inject(MatDialog);

  /**
   * Opens a dialog with default dark theme configuration.
   * @param component The component to display in the dialog
   * @param config Optional dialog configuration overrides
   * @param destroy$ Optional Subject to automatically unsubscribe when component is destroyed
   * @returns Observable of the dialog result
   */
  open<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>,
    destroy$?: Subject<void>
  ): Observable<R | undefined> {
    const dialogRef = this.dialog.open<T, D, R>(component, {
      ...DIALOG_CONFIG,
      ...config
    });

    return this.handleDialogClose(dialogRef, destroy$);
  }

  /**
   * Opens a small dialog (400px width).
   */
  openSmall<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>,
    destroy$?: Subject<void>
  ): Observable<R | undefined> {
    const dialogRef = this.dialog.open<T, D, R>(component, {
      ...SMALL_DIALOG_CONFIG,
      ...config
    });

    return this.handleDialogClose(dialogRef, destroy$);
  }

  /**
   * Opens a medium dialog (600px width).
   */
  openMedium<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>,
    destroy$?: Subject<void>
  ): Observable<R | undefined> {
    const dialogRef = this.dialog.open<T, D, R>(component, {
      ...MEDIUM_DIALOG_CONFIG,
      ...config
    });

    return this.handleDialogClose(dialogRef, destroy$);
  }

  /**
   * Opens a large dialog (800px width).
   */
  openLarge<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>,
    destroy$?: Subject<void>
  ): Observable<R | undefined> {
    const dialogRef = this.dialog.open<T, D, R>(component, {
      ...LARGE_DIALOG_CONFIG,
      ...config
    });

    return this.handleDialogClose(dialogRef, destroy$);
  }

  /**
   * Opens a confirmation dialog.
   * @param title Dialog title
   * @param message Dialog message
   * @param options Additional options
   * @param destroy$ Optional Subject for cleanup
   */
  confirm(
    title: string,
    message: string,
    options?: {
      confirmText?: string;
      cancelText?: string;
      confirmColor?: 'primary' | 'accent' | 'warn';
      icon?: string;
    },
    destroy$?: Subject<void>
  ): Observable<boolean> {
    // Import dynamically to avoid circular dependencies
    return new Observable(observer => {
      import('../../shared/components/confirm-dialog/confirm-dialog.component').then(module => {
        const dialogRef = this.dialog.open(module.ConfirmDialogComponent, {
          ...SMALL_DIALOG_CONFIG,
          disableClose: true,
          data: {
            title,
            message,
            ...options
          }
        });

        const afterClosed$ = destroy$
          ? dialogRef.afterClosed().pipe(takeUntil(destroy$))
          : dialogRef.afterClosed();

        afterClosed$.subscribe(result => {
          observer.next(!!result);
          observer.complete();
        });
      });
    });
  }

  /**
   * Opens a delete confirmation dialog.
   */
  confirmDelete(
    itemName: string,
    destroy$?: Subject<void>
  ): Observable<boolean> {
    return this.confirm(
      'Confirmar Eliminación',
      `¿Estás seguro de que deseas eliminar "${itemName}"? Esta acción no se puede deshacer.`,
      {
        confirmText: 'Eliminar',
        confirmColor: 'warn',
        icon: 'delete'
      },
      destroy$
    );
  }

  /**
   * Gets a raw dialog reference for advanced use cases.
   */
  getDialogRef<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>
  ): MatDialogRef<T, R> {
    return this.dialog.open<T, D, R>(component, {
      ...DIALOG_CONFIG,
      ...config
    });
  }

  /**
   * Closes all open dialogs.
   */
  closeAll(): void {
    this.dialog.closeAll();
  }

  private handleDialogClose<T, R>(
    dialogRef: MatDialogRef<T, R>,
    destroy$?: Subject<void>
  ): Observable<R | undefined> {
    if (destroy$) {
      return dialogRef.afterClosed().pipe(takeUntil(destroy$));
    }
    return dialogRef.afterClosed();
  }
}
